import { FC, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import * as yup from 'yup';
import { Dialog, Typography, FormControlLabel, Radio, RadioGroup, FormControl } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Checkbox } from 'shared/ui/Checkbox';
import { getPartners } from 'entities/Partners/api';
import { retryImportFile } from 'entities/ImportSessions/api';
import { FileType } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { useCustomModelStore } from 'widgets/customModel/state';
import { useFilesStore } from 'widgets/editFile/model/state';
import FormAutocomplete from 'shared/ui/autocomplete/form-autocomplete';
import { AutocompleteOption } from 'shared/ui/autocomplete/Autocomplete';
import { useCustomSearchParams } from 'shared/hooks/use-custom-search';
import { usePartnersStore } from 'widgets/Partners/state';

const PartnersSchema = yup.object({
	partnerId: yup
		.object({
			value: yup.string().notRequired(),
			label: yup.string().notRequired(),
		})
		.nullable(),
});

interface PartnersModalPropsType {
	open: boolean;
	handleClose: () => void;
	file?: FileType;
}

const PartnersModal: FC<PartnersModalPropsType> = ({ open, handleClose, file }) => {
	const { fileId } = useParams();
	const { t } = useTranslation();
	const { searchQueries, changeSearchQueries } = useCustomSearchParams();
	const { partners, setPartnersData } = usePartnersStore();
	const [selectedPartner, setSelectedPartner] = useState<AutocompleteOption | null>(null);
	const [usePartnersAssertions, setUsePartnersAssertions] = useState(true);
	const [partnersList, setPartnersList] = useState<{ value: string; label: string }[]>([]);
	const [selectManuallyPartner, setSelectManuallyPartner] = useState(false);
	const { setFileDetails } = useFilesStore();
	const { setCustomFileDetails } = useCustomModelStore();

	const defaultValueForPartners = useMemo(
		() => partnersList?.find((item) => item.value === file?.partnerId),
		[partnersList, file],
	);

	const isPartnerCandidatesVisible = useMemo(
		() => file?.partnerCandidates?.length && !file.partnerId,
		[file],
	);

	const partnerIds = useMemo(() => {
		return file?.partnerCandidates || (file?.partnerId && [file?.partnerId]) || undefined;
	}, [file]);

	const { data: partnersResponseData, isLoading: getPartnersIsLoading } = useQuery({
		queryKey: ['get/partners', partnerIds, searchQueries],
		queryFn: () =>
			getPartners({
				type: null,
				partnerIds: !open ? partnerIds : undefined,
				...searchQueries,
			}),
		onSuccess: (response) => {
			const mappedPartners = response?.data.results.map((item) => ({
				label: item.name,
				value: item._id,
			}));
			const newList = [...partnersList, ...mappedPartners];
			const uniqueMap = new Map(newList.map((item) => [item.value, item]));
			const uniqueArray = Array.from(uniqueMap.values());
			setPartnersList(uniqueArray);
			if (!partners) {
				setPartnersData(response.data);
			}
		},
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({ message: errorMessage, variant: 'error' });
		},
	});

	const selectedPartnerObject = useMemo(
		() => partnersResponseData?.data.results?.find((item) => item._id === selectedPartner?.value),
		[partnersResponseData, selectedPartner],
	);

	useEffect(() => {
		if (isPartnerCandidatesVisible) {
			setSelectManuallyPartner(true);
		} else {
			setSelectManuallyPartner(false);
		}
	}, [isPartnerCandidatesVisible]);

	const { mutate, isLoading } = useMutation({
		mutationKey: ['patch/files/recognition-result'],
		mutationFn: retryImportFile,
	});

	const handleSubmit = (data: yup.InferType<typeof PartnersSchema>) => {
		mutate(
			{
				id: file?._id,
				data: {
					recognizedLabels: file?.recognizedLabels || [],
					assertionSetsId:
						selectedPartnerObject?.assertionSetsId && usePartnersAssertions
							? selectedPartnerObject?.assertionSetsId
							: file?.assertionSetsId || null,
					partnerId: data?.partnerId?.value || null,
					isPreview: true,
				},
			},
			{
				onSuccess: (response) => {
					enqueueSnackbar({
						message: t('partners.snackbar.success_settings_applying'),
						variant: 'success',
					});
					if (fileId) {
						setFileDetails(response.data);
					} else {
						setCustomFileDetails(response.data);
					}
					handleClose();
				},
				onError: (err) => {
					const errorMessage = getErrorMessage(err);
					enqueueSnackbar({ message: errorMessage, variant: 'error' });
				},
			},
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			PaperProps={{
				title: t('partners.title') as string,
			}}
		>
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '34px' }}>
				{t('partners.select_partner')}
			</Typography>

			<Formik
				initialValues={{
					partnerId: defaultValueForPartners || '',
				}}
				validationSchema={PartnersSchema}
				onSubmit={handleSubmit}
			>
				{({ values, setFieldValue }) => (
					<Form>
						{selectManuallyPartner ? (
							<FormControl
								sx={{ maxHeight: '250px', width: '100%', overflow: 'auto', paddingX: 3 }}
							>
								<RadioGroup
									name="partnerId"
									value={values.partnerId?.value}
									onChange={(e) =>
										setFieldValue('partnerId', {
											value: e.target.value,
											label: '',
										})
									}
								>
									{partnersList.map((item) => (
										<FormControlLabel
											key={item.value}
											value={item.value}
											control={<Radio size="small" />}
											label={item.label}
										/>
									))}
								</RadioGroup>
							</FormControl>
						) : (
							<FormAutocomplete
								name="partnerId"
								defaultValue={defaultValueForPartners}
								options={partnersList}
								label={t('partners.labels.client_supplier') as string}
								placeholder={t('partners.placeholder.client_supplier') as string}
								onSearch={(value) => {
									changeSearchQueries({ search: value, page: 1 });
									setPartnersList([]);
								}}
								onChange={(value) => {
									setSelectedPartner(value as AutocompleteOption);
									setFieldValue('partnerId', (value as AutocompleteOption).value);
								}}
								sx={{ margin: '0' }}
								loadMore={
									partnersResponseData
										? partnersList.length < partnersResponseData.data.totalCount
										: false
								}
								onFetchMore={() => changeSearchQueries({ page: searchQueries.page + 1 })}
								loading={getPartnersIsLoading}
							/>
						)}

						{isPartnerCandidatesVisible && (
							<Typography
								variant={'body2'}
								color={ColorPalette.royalBlue}
								onClick={() => setSelectManuallyPartner(!selectManuallyPartner)}
								sx={{
									cursor: 'pointer',
									margin: '20px 0',
								}}
							>
								{selectManuallyPartner
									? t('partners.select_manually')
									: t('partners.check_proposals')}
							</Typography>
						)}

						{selectedPartnerObject?.assertionSetsId && (
							<FormControlLabel
								control={
									<Checkbox
										checked={usePartnersAssertions}
										onChange={() => {
											setUsePartnersAssertions(!usePartnersAssertions);
										}}
									/>
								}
								label={
									<Typography
										variant={'body2'}
										color={ColorPalette.baliHai}
										sx={{ marginLeft: '10px' }}
									>
										{t('partners.assertions_checkbox')}
									</Typography>
								}
								sx={{ margin: '0', marginTop: 2 }}
							/>
						)}
						<ActionsForDialog
							cancelLabel={t('shared_texts.cancel')}
							cancelAction={handleClose}
							applyLabel={t('shared_texts.continue')}
							applyActionDisabled={isLoading}
							sx={{ marginTop: 5 }}
						/>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default PartnersModal;
